import { Key, useMemo, useState } from "react";
import {
	Button,
	Card,
	Collapse,
	Empty,
	Form,
	Input,
	InputNumber,
	Popconfirm,
	Select,
	Table,
	Tabs
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
	CaretRightOutlined,
	DeleteOutlined,
	EyeOutlined,
	PlusOutlined,
} from "@ant-design/icons";
import { UploadDocument } from "@qualitas/upload-document";

import useAdministration from "../../Hooks/useAdministration/useAdministration";
import {
	ContractDTO,
	SharesType,
	TemplateAdministrationLanguage,
	TemplateFieldValue,
	TemplateAdministrationTemplate
} from "../../Models";

import { IAdministrationProps } from "./IAdministration";
import OfficeUsersAdmin from "./OfficeUsersAdmin";
import PlacementAgentsAdmin from "./PlacementAgentsAdmin";
import TranslationsAdmin from "./TranslationsAdmin";
import UserPermissionsAdmin from "./UserPermissionsAdmin";

import "./Administration.scss";

const staticDocuments: Array<{
	key: string;
	labelKey: string;
	includesNav?: boolean;
}> = [
	{ key: "minorista", labelKey: "adminSharedTemplateRetail" },
	{ key: "investor_classification", labelKey: "adminSharedTemplateInvestorClassification" },
	{ key: "convenience_test", labelKey: "adminSharedTemplateSuitabilityTest", includesNav: true }
];

const Administration = ({
	baseURL,
	baseURLExternal,
	clientId,
	translations,
	translationsConfiguration,
	saveTranslations,
	createTranslationLanguage,
	translationsLoading,
	moduleRole
}: IAdministrationProps) => {
	const t = (key: string, fallback: string) => translations[key] || fallback;
	const [searchText, setSearchText] = useState("");
	const [previewContractIds, setPreviewContractIds] = useState<Record<number, number | null>>({});
	const [previewShareTypeIds, setPreviewShareTypeIds] = useState<Record<number, number | null>>({});
	const [selectedVehicleDocumentId, setSelectedVehicleDocumentId] = useState<number | null>(null);
	const [expandedTemplateRowKeys, setExpandedTemplateRowKeys] = useState<Key[]>([]);
	const {
		contextHolder,
		data,
		form,
		handleLoadTemplateFieldsPreview,
		loadOfficesData,
		loadPlacementAgentsData,
		loadPreviewData,
		loadSharedTemplatesData,
		loadVehicleDocumentsData,
		loadShareTypes,
		loadTemplateDocuments,
		getVehicleDocument,
		getSharedTemplate,
		handleCreateTemplate,
		handleDeleteInstitutionVehicle,
		handleDeleteOffice,
		handleDeleteOfficeUser,
		handleDeleteTemplate,
		handleImportAdministrationFile,
		handleImportDfiFile,
		handleImportStaticFile,
		handlePreviewTemplate,
		handleSaveInstitutionVehicle,
		handleSaveOffice,
		handleSaveOfficeUser,
		isReadOnly,
		loading,
		previewing,
		previewingTemplateId,
		previewContracts,
		saving,
		shareTypes,
		sharedTemplatesLoading,
		vehicleDocumentsLoading,
		templateFieldsForm,
		templateFieldsLoading,
		templateFieldsPreview,
		templateDocumentsLoadingIds,
		templateNameRules
	} = useAdministration({ baseURL, baseURLExternal, clientId, translations, moduleRole });

	const selectedLegalAttachmentId = Form.useWatch("idLegalAttachment", form);
	const selectedVehicle = useMemo(
		() => data.vehicles.find(vehicle => vehicle.id === selectedVehicleDocumentId),
		[data.vehicles, selectedVehicleDocumentId]
	);
	const filteredShareTypes = useMemo(
		() => shareTypes
			.filter((shareType: SharesType) => shareType.idVehicle === selectedVehicleDocumentId)
			.sort((a: SharesType, b: SharesType) => a.sharesType.localeCompare(b.sharesType)),
		[selectedVehicleDocumentId, shareTypes]
	);

	const filteredTemplates = useMemo(() => {
		const normalizedSearch = searchText.trim().toLowerCase();
		if (!normalizedSearch) {
			return data.templates;
		}

		return data.templates.filter((template) => [
			template.name,
			template.vehicleName,
			template.personTypeName,
			template.languageCode,
			template.languageName,
			template.legalAttachmentName
		]
			.filter(Boolean)
			.some(value => value.toLowerCase().includes(normalizedSearch)));
	}, [data.templates, searchText]);

	const getLocalizedPersonTypeLabel = (idPersonType?: number | null, personTypeName?: string | null) => {
		if (idPersonType === 1) {
			return t("fisica", "Física");
		}

		if (idPersonType === 2) {
			return t("juridica", "Jurídica");
		}

		const normalizedName = (personTypeName || "").trim().toLowerCase();
		if (normalizedName.includes("physical") || normalizedName.includes("física") || normalizedName.includes("fisica")) {
			return t("fisica", "Física");
		}

		if (normalizedName.includes("legal") || normalizedName.includes("jurídica") || normalizedName.includes("juridica")) {
			return t("juridica", "Jurídica");
		}

		return personTypeName || "-";
	};

	const getLocalizedLanguageLabel = (languageCode?: string | null, languageName?: string | null) => {
		const normalizedCode = (languageCode || "").trim().toLowerCase();

		if (normalizedCode === "es") {
			return t("spanish", "Español");
		}

		if (normalizedCode === "en") {
			return t("english", "Inglés");
		}

		if (normalizedCode === "pt") {
			return t("portuguese", "Portugués");
		}

		return languageName || languageCode || "-";
	};

	const matchesPreviewContractPersonType = (contract: ContractDTO, templatePersonTypeId: number) => {
		const rawPersonType = String(contract.idPersonType ?? "").trim().toLowerCase();
		const numericPersonType = Number(rawPersonType);

		if (!Number.isNaN(numericPersonType) && numericPersonType > 0) {
			return numericPersonType === templatePersonTypeId;
		}

		if (templatePersonTypeId === 1) {
			return rawPersonType === "1" || rawPersonType.includes("physical") || rawPersonType.includes("física") || rawPersonType.includes("fisica");
		}

		if (templatePersonTypeId === 2) {
			return rawPersonType === "2" || rawPersonType.includes("legal") || rawPersonType.includes("jurídica") || rawPersonType.includes("juridica");
		}

		return false;
	};

	const matchesPreviewContractLanguage = (contract: ContractDTO, template: TemplateAdministrationTemplate) => {
		if (typeof contract.idLanguage === "number" && contract.idLanguage > 0) {
			return contract.idLanguage === template.idLanguage;
		}

		const normalizedContractLanguageCode = String(contract.languageCode ?? contract.languageName ?? "")
			.trim()
			.toLowerCase();
		const normalizedTemplateLanguageCode = String(template.languageCode ?? template.languageName ?? "")
			.trim()
			.toLowerCase();

		if (normalizedContractLanguageCode && normalizedTemplateLanguageCode) {
			return normalizedContractLanguageCode === normalizedTemplateLanguageCode;
		}

		return false;
	};

	const filteredExpandedTemplateRowKeys = useMemo(() => (
		expandedTemplateRowKeys.filter(key => filteredTemplates.some(template => template.id === key))
	), [expandedTemplateRowKeys, filteredTemplates]);

	const handleTabChange = (key: string) => {
		if (key === "institution-vehicle") {
			loadPlacementAgentsData();
		}

		if (key === "offices") {
			loadOfficesData();
		}

		if (key === "vehicle-documents") {
			loadShareTypes();
		}

		if (key === "shared-templates") {
			loadSharedTemplatesData();
		}
	};

	const renderTemplateUploader = (
		record: TemplateAdministrationTemplate,
		kind: string,
		label: string,
		uploadedFile?: string,
		disabled?: boolean,
		uploadTooltip?: string
	) => (
		<div className="templates-table-upload">
			<UploadDocument
				baseURL={baseURL}
				clientId={clientId}
				moduleRole={moduleRole}
				translations={{
					label,
					uploadDocument: t("uploadDocument", "Subir documento"),
					uploadDocumentTooltip: uploadTooltip || t("allowedFileFormatsAdmin", "Formatos permitidos"),
					selectFiles: t("selectFiles", "Seleccionar archivos"),
					changeFile: t("changeFile", "Cambiar archivo")
				}}
				mandatory={false}
				oneRow={false}
				uploadedFile={uploadedFile}
				disabled={isReadOnly || disabled}
				error={false}
				shouldRunOcr={false}
				ocrFields={[]}
				updateFileUrl={(fileUrl: string) => handleImportAdministrationFile(kind, record.name, fileUrl)}
				extraFormatsAllowed=".docx"
			/>
		</div>
	);

	const columns: ColumnsType<TemplateAdministrationTemplate> = [
		{
			title: t("template", "Plantilla"),
			key: "name",
			render: (_, record) => (
				<div className="templates-template-cell">
					<span className="templates-name-tag">{record.name}</span>
				</div>
			)
		},
		{
			title: t("details", "Detalles"),
			key: "details",
			render: (_, record) => (
				<div className="templates-details-cell">
					<div className="templates-details-summary">
						{record.vehicleName} · {getLocalizedPersonTypeLabel(record.idPersonType, record.personTypeName)} · {record.languageCode.toUpperCase()} · {getLocalizedLanguageLabel(record.languageCode, record.languageName)}
					</div>
					{record.legalAttachmentName ? (
						<div className="templates-details-muted">
							{t("adminAttachment", "Documento legal")}: {record.legalAttachmentName}
						</div>
					) : (
						<div className="templates-details-muted">
							{t("adminAttachment", "Documento legal")}: -
						</div>
					)}
				</div>
			)
		},
		{
			title: t("adminPreviewTitle", "Probar plantillas"),
			key: "preview",
			width: 260,
			render: (_, record) => (
				<div
					className="templates-preview-inline"
					onClick={(event) => event.stopPropagation()}
				>
					<Select
						showSearch
						optionFilterProp="label"
						placeholder={t("adminPreviewShareTypePlaceholder", "Selecciona un share type")}
						value={previewShareTypeIds[record.id] ?? undefined}
						onFocus={loadPreviewData}
						options={shareTypes
							.filter((shareType: SharesType) => shareType.idVehicle === record.idVehicle)
							.sort((a: SharesType, b: SharesType) => a.sharesType.localeCompare(b.sharesType))
							.map((shareType: SharesType) => ({
								value: shareType.idSharesType,
								label: shareType.sharesType
							}))}
						onChange={(value) => setPreviewShareTypeIds(prevState => ({
							...prevState,
							[record.id]: typeof value === "number" ? value : null
						}))}
					/>
					<Select
						showSearch
						optionFilterProp="label"
						placeholder={t("adminPreviewContractPlaceholder", "Selecciona un contrato firmado")}
						value={previewContractIds[record.id] ?? undefined}
						onFocus={loadPreviewData}
						options={previewContracts
							.filter((contract: ContractDTO) => (
								matchesPreviewContractPersonType(contract, record.idPersonType)
								&& matchesPreviewContractLanguage(contract, record)
							))
							.map((contract: ContractDTO) => ({
								value: contract.id,
								label: `${contract.id} - ${contract.investorName || "-"}`
							}))}
						onChange={(value) => setPreviewContractIds(prevState => ({
							...prevState,
							[record.id]: typeof value === "number" ? value : null
						}))}
					/>
					<Button
						type="default"
						icon={<EyeOutlined />}
						loading={previewing && previewingTemplateId === record.id}
						disabled={
							!previewContractIds[record.id]
							|| (
								shareTypes.some((shareType: SharesType) => shareType.idVehicle === record.idVehicle)
								&& !previewShareTypeIds[record.id]
							)
						}
						onClick={() => {
							const contractId = previewContractIds[record.id];
							const shareTypeId = previewShareTypeIds[record.id] ?? undefined;
							if (!contractId) {
								return;
							}

							handlePreviewTemplate(record.id, contractId, shareTypeId);
						}}
					>
						{t("adminPreviewButton", "Vista previa")}
					</Button>
				</div>
			)
		}
	];

	const templateFieldColumns: ColumnsType<TemplateFieldValue> = [
		{
			title: t("field", "Campo"),
			dataIndex: "key",
			key: "key",
			width: "40%",
			render: (value: string) => <code>{value}</code>
		},
		{
			title: t("value", "Valor"),
			dataIndex: "value",
			key: "value",
			render: (value: string) => value || "-"
		}
	];

	const guideItems = [
		{
			key: "templates",
			title: t("templates", "Plantillas"),
			description: t("adminGuideTemplates", "En esta pantalla se da de alta cada plantilla principal del sistema. Al crear una plantilla debes indicar su nombre, el vehículo, el tipo de persona y el idioma, porque esos datos determinan qué contrato puede usarla. Después, en la tabla de la derecha, puedes cargar el Word principal con campos, la versión vacía en PDF y el documento legal asociado. También sirve para probar rápidamente una plantilla con un contrato ya firmado y comprobar que la generación del PDF sale como esperas antes de usarla en un flujo real.")
		},
		{
			key: "shared-templates",
			title: t("adminSharedTemplates", "Plantillas compartidas"),
			description: t("adminGuideSharedTemplates", "Esta pestaña recoge documentos comunes que no dependen de un vehículo concreto, pero sí del idioma. Aquí se mantienen piezas reutilizables como la declaración de riesgos, la clasificación del inversor o el test de conveniencia. Lo habitual es subir una versión por idioma para que el sistema pueda adjuntarla o mostrarla correctamente según la plantilla o el proceso en curso.")
		},
		{
			key: "vehicle-documents",
			title: t("adminVehicleDocumentsTab", "DFIs"),
			description: t("adminGuideDfis", "La pantalla de DFIs está pensada para los documentos informativos que sí dependen del vehículo y del tipo de participación. Primero seleccionas un vehículo y, a continuación, se muestran los share types configurados para ese vehículo. Para cada uno puedes subir su DFI correspondiente. Esto permite que el sistema utilice el documento correcto según el producto que esté contratando el inversor.")
		},
		{
			key: "template-fields",
			title: t("adminTemplateFieldsTab", "Campos de plantillas"),
			description: t("adminGuideTemplateFields", "Esta pestaña es una ayuda de diagnóstico. Introduciendo un IdContract puedes ver qué campos se están construyendo realmente para rellenar los templates y qué valor lleva cada uno. Es especialmente útil para revisar sustituciones, detectar si falta una traducción, comprobar datos procedentes de vistas SQL o entender por qué un placeholder concreto no se está rellenando como debería.")
		},
		{
			key: "institution-vehicle",
			title: t("adminPlacementAgentsTab", "Agentes colocadores"),
			description: t("adminGuidePlacementAgents", "Aquí se configuran las relaciones entre instituciones y vehículos. No solo define qué vehículo puede comercializar cada agente colocador, sino también reglas funcionales que afectan al proceso, como AFIN, FATCA, CRS, suitability test, investor status o determinadas validaciones adicionales. En la práctica, esta pestaña condiciona qué experiencia tendrá el usuario al crear y completar contratos para una institución concreta.")
		},
		{
			key: "offices",
			title: t("adminOfficesTab", "Oficinas"),
			description: t("adminGuideOffices", "La sección de oficinas permite organizar la estructura operativa de cada institución. Desde aquí se crean oficinas, se marca si una es central y se asignan usuarios a cada una. Esa configuración afecta al alcance con el que luego cada usuario ve contratos, plantillas o datos de administración, por lo que conviene mantenerla alineada con la estructura real del negocio.")
		},
		{
			key: "user-permissions",
			title: t("adminUserPermissionsTab", "Permisos de usuario"),
			description: t("adminGuideUserPermissions", "Esta pantalla sirve para administrar el acceso al sistema. Desde ella puedes revisar o modificar el tipo de usuario, la institución asociada, los módulos habilitados y los datos de contacto básicos. Es el punto adecuado cuando una persona no ve una sección que debería ver, cuando hay que dar acceso a una nueva funcionalidad o cuando necesitas revisar la configuración de permisos de un usuario existente.")
		},
		{
			key: "translations",
			title: t("adminTranslationsTab", "Traducciones"),
			description: t("adminGuideTranslations", "En traducciones se gestiona el JSON general que utiliza tanto el front como el back. Desde aquí puedes crear nuevos idiomas y editar directamente las claves existentes para etiquetas, mensajes y catálogos traducibles. Es importante usar esta pestaña cuando aparezcan textos sin traducir, cuando se quiera añadir un nuevo idioma o cuando haya que mantener sincronizado el contenido que luego se utiliza en formularios, administración y templates.")
		}
	];

	return (
		<div className="administration-page">
			{contextHolder}
			<div className="administration-shell">
				<div className="administration-header">
					<div>
						<h1>{translations.Administration || t("administration", "Administración")}</h1>
						<p>
							{t("adminTemplatesDescription", "Gestiona las plantillas Word, anexos legales y documentos estáticos usados para generar contratos y vistas previas.")}
						</p>
					</div>
				</div>

				<Tabs
					className="templates-tab"
					onChange={handleTabChange}
					items={[
						{
							key: "templates",
							label: t("templates", "Plantillas"),
							children: (
								<div className="templates-grid">
									<Card
										className="templates-card"
										title={t("adminNewTemplate", "Nueva plantilla")}
										loading={loading}
									>
										<Form
											layout="vertical"
											form={form}
											onFinish={handleCreateTemplate}
										>
											<Form.Item
												label={t("adminTemplateName", "Nombre de la plantilla")}
												name="name"
												rules={templateNameRules}
											>
												<Input placeholder="AS_PF_ES" disabled={isReadOnly} />
											</Form.Item>

											<Form.Item
												label={t("vehiculo", "Vehículo")}
												name="idVehicle"
												rules={[{ required: true, message: t("adminVehicleRequired", "El vehículo es obligatorio") }]}
											>
												<Select
													disabled={isReadOnly}
													showSearch
													optionFilterProp="label"
													options={data.vehicles.map(vehicle => ({
														value: vehicle.id,
														label: vehicle.name
													}))}
												/>
											</Form.Item>

											<Form.Item
												label={t("tipoPersona", "Tipo de persona")}
												name="idPersonType"
												rules={[{ required: true, message: t("adminPersonTypeRequired", "El tipo de persona es obligatorio") }]}
											>
												<Select
													disabled={isReadOnly}
													options={data.personTypes.map(personType => ({
														value: personType.id,
														label: getLocalizedPersonTypeLabel(personType.id, personType.name)
													}))}
												/>
											</Form.Item>

											<Form.Item
												label={t("language", "Idioma")}
												name="idLanguage"
												rules={[{ required: true, message: t("adminLanguageRequired", "El idioma es obligatorio") }]}
											>
												<Select
													disabled={isReadOnly}
													options={data.languages.map(language => ({
														value: language.id,
														label: `${language.code} · ${getLocalizedLanguageLabel(language.code, language.name)}`
													}))}
												/>
											</Form.Item>

											<Form.Item
												label={t("adminExistingAttachmentLabel", "Documento legal existente (opcional)")}
												name="idLegalAttachment"
											>
												<Select
													disabled={isReadOnly}
													showSearch
													allowClear
													optionFilterProp="label"
													placeholder={t("adminExistingAttachmentOptional", "Selecciona un documento legal existente para reutilizar")}
													options={data.legalAttachments.map(attachment => ({
														value: attachment.id,
														label: attachment.name
													}))}
													onChange={(value) => {
														if (value) {
															form.setFieldValue("legalAttachmentName", undefined);
														}
													}}
												/>
											</Form.Item>

											<Form.Item
												label={t("adminAttachment", "Documento legal")}
												name="legalAttachmentName"
											>
												<Input
													disabled={isReadOnly || !!selectedLegalAttachmentId}
													placeholder={t("adminAttachmentOptional", "Documento legal opcional")}
												/>
											</Form.Item>

											<Form.Item>
												<Button
													type="primary"
													htmlType="submit"
													icon={<PlusOutlined />}
													loading={saving}
													disabled={isReadOnly}
													block
												>
													{t("adminCreateTemplate", "Crear plantilla")}
												</Button>
											</Form.Item>
										</Form>
										<p className="templates-subtle-text">
											{t("adminTemplateNameHelp", "El nombre de la plantilla debe coincidir con el nombre del documento Word en Blob Storage, sin la extensión .docx.")}
										</p>
									</Card>

									<div className="templates-right-column">
										<Card
											className="templates-table-card"
											title={t("adminExistingTemplates", "Plantillas existentes")}
											loading={loading}
										>
											<Input
												className="templates-search-input"
												placeholder={t("search", "Buscar")}
												value={searchText}
												onChange={(event) => setSearchText(event.target.value)}
												allowClear
											/>
											<Table
												rowKey="id"
												dataSource={filteredTemplates}
												columns={columns}
												pagination={{ pageSize: 10, showSizeChanger: true }}
												expandable={{
													expandedRowKeys: filteredExpandedTemplateRowKeys,
													onExpand: (expanded, record) => {
														if (expanded) {
															loadTemplateDocuments(record.id);
														}

														setExpandedTemplateRowKeys(prevState => (
															expanded
																? [...prevState.filter(key => key !== record.id), record.id]
																: prevState.filter(key => key !== record.id)
														));
													},
													expandRowByClick: true,
													columnWidth: 44,
													expandIcon: ({ expanded, onExpand, record }) => (
														<Button
															type="text"
															size="small"
															className="templates-expand-button"
															icon={<CaretRightOutlined rotate={expanded ? 90 : 0} />}
															onClick={(event) => onExpand(record, event)}
														/>
													),
													expandedRowRender: (record) => (
														<div className="templates-expanded-row">
															<div className="templates-documents-cell">
																{templateDocumentsLoadingIds.includes(record.id) && (
																	<div className="templates-details-muted">
																		{t("loading", "Cargando")}...
																	</div>
																)}
																{renderTemplateUploader(
																	record,
																	"template",
																	t("adminUploadTemplate", "Plantilla con campos (.docx)"),
																	record.mainDocumentUrl
																)}
																{renderTemplateUploader(
																	record,
																	"empty_template",
																	t("adminEmptyTemplate", "Plantilla vacía (.pdf)"),
																	record.emptyMainDocumentUrl
																)}
																{renderTemplateUploader(
																	record,
																	"attachment",
																	t("adminUploadAttachment", "Documento legal (.pdf)"),
																	record.legalAttachmentUrl,
																	!record.legalAttachmentName
																)}
															</div>
															{!isReadOnly && (
																<div className="templates-expanded-actions">
																	<Popconfirm
																		title={t("adminDeleteTemplateConfirm", "¿Quieres eliminar esta plantilla?")}
																		onConfirm={() => handleDeleteTemplate(record.id)}
																	>
																		<Button danger size="small" icon={<DeleteOutlined />}>
																			{t("delete", "Eliminar")}
																		</Button>
																	</Popconfirm>
																</div>
															)}
														</div>
													)
												}}
											/>
										</Card>
									</div>
								</div>
							)
						},
						{
							key: "shared-templates",
							label: t("adminSharedTemplates", "Plantillas compartidas"),
							children: (
								<Card
									className="templates-static-card"
									title={t("adminSharedTemplates", "Plantillas compartidas")}
									loading={loading || sharedTemplatesLoading}
								>
									<div className="templates-static-list">
										{staticDocuments.map(document => (
												<div key={document.key} className="templates-static-row">
													<div className="templates-static-title">{t(document.labelKey, document.key)}</div>
												{data.languages.map((language: TemplateAdministrationLanguage) => (
													<div key={`${document.key}_${language.id}`} className="templates-static-language-row">
														<div className="templates-static-upload">
															<UploadDocument
																baseURL={baseURL}
																clientId={clientId}
																moduleRole={moduleRole}
																translations={{
																	label: language.code,
																	uploadDocument: t("uploadDocument", "Subir documento"),
																	uploadDocumentTooltip: t("allowedWordFormatsAdmin", "Formatos permitidos: DOCX. Tamaño máximo: 25MB."),
																	selectFiles: t("selectFiles", "Seleccionar archivos"),
																	changeFile: t("changeFile", "Cambiar archivo")
																}}
																mandatory={false}
																oneRow={false}
																uploadedFile={getSharedTemplate(document.key, language.id)?.url}
																disabled={isReadOnly}
																error={false}
																shouldRunOcr={false}
																ocrFields={[]}
																updateFileUrl={(fileUrl: string) => handleImportStaticFile(document.key, fileUrl, language.id)}
																extraFormatsAllowed=".docx"
															/>
														</div>
													</div>
												))}
												{document.key === "convenience_test" && (
													<div key={`${document.key}_feeder_minorista`} className="templates-static-language-row">
														<div className="templates-static-upload templates-static-upload-long-label">
															<UploadDocument
																baseURL={baseURL}
																clientId={clientId}
																moduleRole={moduleRole}
																translations={{
																	label: t("adminSharedTemplateSuitabilityTestPt", "Feeder Minorista"),
																	uploadDocument: t("uploadDocument", "Subir documento"),
																	uploadDocumentTooltip: t("allowedWordFormatsAdmin", "Formatos permitidos: DOCX. Tamaño máximo: 25MB."),
																	selectFiles: t("selectFiles", "Seleccionar archivos"),
																	changeFile: t("changeFile", "Cambiar archivo")
																}}
																mandatory={false}
																oneRow={false}
																uploadedFile={getSharedTemplate("suitability_test_pt", 3)?.url}
																disabled={isReadOnly}
																error={false}
																shouldRunOcr={false}
																ocrFields={[]}
																updateFileUrl={(fileUrl: string) => handleImportStaticFile("suitability_test_pt", fileUrl, 3)}
																extraFormatsAllowed=".docx"
															/>
														</div>
													</div>
												)}
												{document.includesNav && (
													<div key={`${document.key}_nav`} className="templates-static-language-row">
														<div className="templates-static-upload">
															<UploadDocument
																baseURL={baseURL}
																clientId={clientId}
																moduleRole={moduleRole}
																translations={{
																	label: "NAV",
																	uploadDocument: t("uploadDocument", "Subir documento"),
																	uploadDocumentTooltip: t("allowedWordFormatsAdmin", "Formatos permitidos: DOCX. Tamaño máximo: 25MB."),
																	selectFiles: t("selectFiles", "Seleccionar archivos"),
																	changeFile: t("changeFile", "Cambiar archivo")
																}}
																mandatory={false}
																oneRow={false}
																uploadedFile={getSharedTemplate("convenience_test_nav", 1)?.url}
																disabled={isReadOnly}
																error={false}
																shouldRunOcr={false}
																ocrFields={[]}
																updateFileUrl={(fileUrl: string) => handleImportStaticFile("convenience_test_nav", fileUrl, 1)}
																extraFormatsAllowed=".docx"
															/>
														</div>
													</div>
												)}
											</div>
										))}
									</div>
								</Card>
							)
						},
						{
							key: "vehicle-documents",
							label: t("adminVehicleDocumentsTab", "DFIs"),
							children: (
								<Card
									className="templates-static-card"
									title={t("adminVehicleDocumentsTitle", "DFIs")}
									loading={loading || vehicleDocumentsLoading}
								>
									<div className="templates-vehicle-documents">
										<Select
											value={selectedVehicleDocumentId ?? undefined}
											onChange={(idVehicle: number) => {
												setSelectedVehicleDocumentId(idVehicle);
												loadVehicleDocumentsData(idVehicle);
											}}
											placeholder={t("adminSelectVehicle", "Selecciona un vehículo")}
											showSearch
											optionFilterProp="label"
											options={data.vehicles.map(vehicle => ({
												value: vehicle.id,
												label: vehicle.name
											}))}
										/>

										{selectedVehicle && (
											<div className="templates-static-row">
												<div className="templates-static-title">
													{t("adminDfiDocuments", "DFIs")} · {selectedVehicle.name}
												</div>
												{filteredShareTypes.length === 0 ? (
													<div className="templates-details-muted">
														{t("adminNoShareTypes", "No hay tipos de participación configurados para este vehículo")}
													</div>
												) : (
													filteredShareTypes.map((shareType: SharesType) => (
														<div key={shareType.idSharesType} className="templates-static-language-row">
															<div className="templates-static-upload">
																<UploadDocument
																	baseURL={baseURL}
																	clientId={clientId}
																	moduleRole={moduleRole}
																	translations={{
																		label: shareType.sharesType,
																		uploadDocument: t("uploadDocument", "Subir documento"),
																		uploadDocumentTooltip: t("allowedFileFormatsAdmin", "Formatos permitidos"),
																		selectFiles: t("selectFiles", "Seleccionar archivos"),
																		changeFile: t("changeFile", "Cambiar archivo")
																	}}
																	mandatory={false}
																	oneRow={false}
																	uploadedFile={getVehicleDocument("DFI", selectedVehicle.id, shareType.idSharesType)?.url}
																	disabled={isReadOnly}
																	error={false}
																	shouldRunOcr={false}
																	ocrFields={[]}
																	updateFileUrl={(fileUrl: string) => handleImportDfiFile(selectedVehicle.id, shareType.idSharesType, fileUrl)}
																/>
															</div>
														</div>
													))
												)}
											</div>
										)}
									</div>
								</Card>
							)
						},
						{
							key: "template-fields",
							label: t("adminTemplateFieldsTab", "Campos de plantillas"),
							children: (
								<Card
									className="templates-static-card"
									title={t("adminTemplateFieldsTitle", "Campos de plantillas")}
								>
									<div className="template-fields-description">
										{t("adminTemplateFieldsDescription", "Introduce un contrato existente para ver los valores reales que llegan desde cada vista usada para rellenar las plantillas.")}
									</div>

									<Form
										layout="inline"
										form={templateFieldsForm}
										onFinish={(values) => handleLoadTemplateFieldsPreview(Number(values.contractId))}
										className="template-fields-form"
									>
										<Form.Item
											label={t("adminContractId", "Id del contrato")}
											name="contractId"
											rules={[{ required: true, message: t("adminContractIdRequired", "El id del contrato es obligatorio") }]}
										>
											<InputNumber min={1} controls={false} />
										</Form.Item>
										<Form.Item>
											<Button
												type="primary"
												icon={<EyeOutlined />}
												htmlType="submit"
												loading={templateFieldsLoading}
											>
												{t("adminTemplateFieldsButton", "Ver campos")}
											</Button>
										</Form.Item>
									</Form>

									{templateFieldsPreview ? (
										<div className="template-fields-content">
											<Collapse
												className="template-fields-sections"
												items={templateFieldsPreview.sections.map(section => ({
													key: section.key,
													label: `${section.title} (${section.fields.length})`,
													children: (
														<Table
															size="small"
															rowKey="key"
															columns={templateFieldColumns}
															dataSource={section.fields}
															pagination={false}
														/>
													)
												}))}
											/>
										</div>
									) : (
										<Empty
											className="template-fields-empty"
											description={t("adminTemplateFieldsEmpty", "Selecciona un contrato para ver los campos")}
										/>
									)}
								</Card>
							)
						},
						{
							key: "institution-vehicle",
							label: t("adminPlacementAgentsTab", "Agentes colocadores"),
							children: (
								<PlacementAgentsAdmin
									loading={loading}
									isReadOnly={isReadOnly}
									translations={translations}
									institutions={data.institutions}
									vehicles={data.vehicles}
									institutionVehicles={data.institutionVehicles}
									handleSaveInstitutionVehicle={handleSaveInstitutionVehicle}
									handleDeleteInstitutionVehicle={handleDeleteInstitutionVehicle}
								/>
							)
						},
						{
							key: "offices",
							label: t("adminOfficesTab", "Oficinas"),
							children: (
								<OfficeUsersAdmin
									loading={loading}
									isReadOnly={isReadOnly}
									translations={translations}
									institutions={data.institutions}
									users={data.users}
									offices={data.offices}
									officeUsers={data.officeUsers}
									handleSaveOffice={handleSaveOffice}
									handleDeleteOffice={handleDeleteOffice}
									handleSaveOfficeUser={handleSaveOfficeUser}
									handleDeleteOfficeUser={handleDeleteOfficeUser}
								/>
							)
						},
						{
							key: "user-permissions",
							label: t("adminUserPermissionsTab", "Permisos de usuario"),
							children: (
								<UserPermissionsAdmin
									baseURLExternal={baseURLExternal}
									clientId={clientId}
									moduleRole={moduleRole}
									translations={translations}
								/>
							)
						},
						{
							key: "translations",
							label: t("adminTranslationsTab", "Traducciones"),
							children: (
								<TranslationsAdmin
									isReadOnly={isReadOnly}
									loading={!!translationsLoading}
									translations={translations}
									translationsConfiguration={translationsConfiguration ?? null}
									onSaveTranslations={saveTranslations!}
									onCreateLanguage={createTranslationLanguage!}
								/>
							)
						},
						{
							key: "guide",
							label: t("adminGuideTab", "Guía"),
							children: (
								<Card
									className="templates-static-card"
									title={t("adminGuideTitle", "Guía de administración")}
								>
									<div className="template-fields-description">
										{t("adminGuideDescription", "Esta pestaña resume para qué sirve cada sección del módulo de Administración y cuándo conviene utilizarla.")}
									</div>
									<div className="templates-static-list">
										{guideItems.map(item => (
											<div key={item.key} className="templates-static-row">
												<div className="templates-static-title">{item.title}</div>
												<div className="templates-details-muted">{item.description}</div>
											</div>
										))}
									</div>
									<div className="admin-guide-state-flow">
										<div className="templates-static-title">
											{t("adminGuideContractStateFlowTitle", "Flujo de estados de contratos")}
										</div>
										<img
											src={`${process.env.PUBLIC_URL}/images/BookbuildingStates.svg`}
											alt={t("adminGuideContractStateFlowAlt", "Gráfico del flujo de estados de los contratos")}
										/>
									</div>
								</Card>
							)
						}
					]}
				/>
			</div>
		</div>
	);
};

export default Administration;
